// Core imports
import { useState, useMemo, lazy, Suspense, Fragment, useCallback, useEffect, createContext } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

// Component imports
import { Tabs, Row, Col, Spin, Card, Space, Button, Tooltip, Typography } from 'antd';
import { ArrowLeftIcon } from '../../Icons/ArrowLeftIcon';
import KnowledgeBasesFormLanguageBar from './KnowledgeBasesFormLanguageBar';
import ConflictChangesModal from '../../Modals/ConflictChangesModal';

// Misc imports
import { useTranslation } from 'react-i18next';
import { useFetchKnowledgeBaseArticlesQuery, useFetchKnowledgeBaseGroupsQuery, useFetchKnowledgeBaseQuery, useFetchKnowledgeBasesQuery } from '../../../store/knowledgeBases/knowledgeBasesApi';
import { IS_HRWIZ_CONSOLE_ENABLED } from '../../../constants/env';
import replaceUrlParams from '../../../utils/string/replaceUrlParams';
import { NAV_ORGANIZATION_KNOWLEDGE_BASES } from '../../../constants/navigation';
import { truncate } from '../../../utils/string/truncate';
import { KNOWLEDGE_BASE_URL_PARAMS } from '../../../constants/knowledgeBases';
import userHasEditAccess from '../../../utils/misc/hasEditAccess';
import { KNOWLEDGE_BASE_TYPES } from '../../../constants/main';
import { useDispatch } from 'react-redux';
import { changeSelectedLanguage, updateFormHasPendingChanges, updateKnowledgeBaseId, updateMissingTranslationInfo, updateSelectedArticle } from '../../../store/knowledgeBases/knowledgeBasesActions';
import useBeforeUnload from '../../../utils/hooks/useBeforeUnload';
import { useModalConfirm } from '../../../utils/hooks/useModalConfirm';
import ResizablePanelsWrapper from '../../UI/ResizablePanelsWrapper';
import useTheme from '../../../utils/hooks/useTheme';
import { OPTIMISTIC_LOCKING_EXCEPTION_ERROR_CODE } from '../../../constants/errors';

// Lazy component imports
const LazyKnowledgeBasesArticleTab = lazy(() => import('./KnowledgeBasesArticleTab'));
const LazyKnowledgeBasesSettingsTab = lazy(() => import('./KnowledgeBasesSettingsTab'));
const KnowledgeBasesArticleForm = lazy(() => import('./KnowledgeBasesArticleForm'));
const KnowledgeBasesSettingsForm = lazy(() => import('./KnowledgeBasesSettingsForm'));

// Constants
const DEFAULT_SIDE_PANEL_WIDTH = 340;
const MIN_SIDE_PANEL_WIDTH = 340;
const MAX_SIDE_PANEL_WIDTH = 960;

export const KnowledgeBaseConfirmContext = createContext(null);

/**
 * @typedef {Object} CommonDataForProps
 * @property {boolean} [hasEditAccess] - Represents if the user has edit access
 * @property {boolean} [isKbIntegrationOrShared] - Represents if the current kb is integration or shared
 */

/**
 * Retrieves common properties that are likely needed by multiple tab/content components.
 * This helps in standardizing and reducing repetition when defining props for lazy components.
 * @param {CommonDataForProps} data - The common data bundle.
 * @returns {{hasEditAccess: boolean, isKbIntegrationOrShared: boolean}}
 */
const getCommonProps = (data) => ({
  hasEditAccess: data.hasEditAccess,
  isKbIntegrationOrShared: data.isKbIntegrationOrShared
});

const getArticleTabComponentProps = (data) => {
  return {
    ...getCommonProps(data)
  };
};

const getArticleContentComponentProps = (data) => {
  return {
    ...getCommonProps(data),
    handleUpdateFormInstance: data.handleUpdateFormInstance,
    handleConflictModalState: data.handleConflictModalState
  };
};

const getSettingsTabComponentProps = (data) => ({
  ...getCommonProps(data)
});

const getSettingsContentComponentProps = (data) => ({
  ...getCommonProps(data),
  handleUpdateFormInstance: data.handleUpdateFormInstance,
  handleConflictModalState: data.handleConflictModalState
});

/**
 * Configuration object for the tabs in the form.
 * Each key represents a tab and contains its metadata, lazy-loaded components,
 * and functions to dynamically generate props for those components.
 * - `key`: Unique identifier for the tab.
 * - `getLabel`: Function to get the internationalized tab label.
 * - `LazyTabComponent`: The lazy-loaded component for the tab's representation in the tab bar (left panel).
 * - `LazyContentComponent`: The lazy-loaded component for the main content area when the tab is active (right panel).
 * - `getContentComponentProps`: Function that returns an object of props to be passed to `LazyContentComponent`.
 * - `getTabComponentProps`: Function that returns an object of props to be passed to `LazyTabComponent`.
 * - `disabled`: Function to determine if the tab should be disabled.
 * - `priority`: Number to determine the order of tabs.
 */
const TABS = {
  ARTICLES: {
    key: 'article',
    getLabel: (t) => t ? t('Articles') : 'Articles',
    LazyTabComponent: LazyKnowledgeBasesArticleTab,
    LazyContentComponent: KnowledgeBasesArticleForm,
    getContentComponentProps: getArticleContentComponentProps,
    getTabComponentProps: getArticleTabComponentProps,
    isDataLoading: (loadingStates) =>
      loadingStates.isLoadingKnowledgeBase || loadingStates.isLoadingArticles || loadingStates.isLoadingGroups,
    disabled: (knowledgeBase) => !knowledgeBase?.createdAt,
    priority: 1
  },
  SETTINGS: {
    key: 'settings',
    getLabel: (t) => t ? t('Settings') : 'Settings',
    LazyTabComponent: LazyKnowledgeBasesSettingsTab,
    LazyContentComponent: KnowledgeBasesSettingsForm,
    getContentComponentProps: getSettingsContentComponentProps,
    getTabComponentProps: getSettingsTabComponentProps,
    isDataLoading: (loadingStates) => loadingStates.isLoadingKnowledgeBase || loadingStates.isLoadingKnowledgeBases,
    disabled: false,
    priority: 2
  }
};

/**
 * Renders a centered loading spinner.
 * @param {string} [tip=""] - Text to display below the spinner.
 * @param {string} [height='300px'] - Minimum height for the loading container.
 */
const renderLoadingIndicator = (tip = '', height = '300px') => {
  return (
    <Row className="w-100" style={{ minHeight: height }} justify="center" align="center">
      <Col xs={24} className="d-flex justify-content-center align-items-center">
        <Spin tip={tip} />
      </Col>
    </Row>
  );
};

const KnowledgeBasesForm = ({ knowledgeBaseId }) => {
  const { orgId, tenantId } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isMobileOrTabletSize } = useTheme();
  const { organizationRoles, tenantRoles } = useSelector((state) => state.auth.currentUser);
  const hasEditAccess = userHasEditAccess(orgId, organizationRoles, tenantId, tenantRoles, false);
  const { formHasPendingChanges, selectedLanguage } = useSelector(state => state.knowledgeBases);
  const [activeTabKey, setActiveTabKey] = useState(TABS.ARTICLES.key);
  const [formController, setFormController] = useState(null);
  const [isConflictModalOpen, setIsConflictModalOpen] = useState(false);
  const [conflictData, setConflictData] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);

  const { data: knowledgeBase, isLoading: isLoadingKnowledgeBase } = useFetchKnowledgeBaseQuery({ orgId: orgId, knowledgeBaseId: knowledgeBaseId, urlParams: { includeTenants: true, includeIntegration: true } }, { skip: !knowledgeBaseId });
  const { isLoading: isLoadingArticles } = useFetchKnowledgeBaseArticlesQuery({ orgId, knowledgeBaseId, urlParams: KNOWLEDGE_BASE_URL_PARAMS.ARTICLES }, { skip: !knowledgeBaseId });
  const { isLoading: isLoadingGroups } = useFetchKnowledgeBaseGroupsQuery({ orgId, knowledgeBaseId, urlParams: KNOWLEDGE_BASE_URL_PARAMS.GROUPS }, { skip: !knowledgeBaseId });
  const { isLoading: isLoadingKnowledgeBases } = useFetchKnowledgeBasesQuery({ orgId, urlParams: {
    includeTenants: false,
    includeIntegration: false,
    page: 0,
    pageSize: 1000
  } }, { skip: !orgId || activeTabKey === TABS.ARTICLES.key });

  const handleConflictModalState = (isModalOpen, conflictData, pendingAction) => {
    if(!isModalOpen) {
      handleCancelConflict();
      return;
    }

    handleOpenConflictModal(conflictData, pendingAction);
  };

  useEffect(() => {
    if(knowledgeBaseId) {
      dispatch(updateKnowledgeBaseId(knowledgeBaseId));
    }

    return () => {
      dispatch(updateSelectedArticle({}));
      dispatch(updateFormHasPendingChanges(false));
      dispatch(changeSelectedLanguage(null));
      dispatch(updateKnowledgeBaseId(null));
      dispatch(updateMissingTranslationInfo({}));
    };
  }, [dispatch, knowledgeBaseId]);

  useBeforeUnload(formHasPendingChanges, t('Are you sure you want to leave the page? Any unsaved changes will be lost.'));

  const { showModalConfirm } = useModalConfirm(formHasPendingChanges);

  const handleShowConfirm = (action) => {
    showModalConfirm({
      defaultAction: () => {
        action();
        dispatch(updateFormHasPendingChanges(false));
      },
      onConfirm: async () => {
        if (!formController || !formController.form || !formController.customSubmit) {
          console.error('Form controller is not ready.');
          return;
        }

        try {
          const values = await formController.form.validateFields();
          const response = await formController.customSubmit(values, { includeVersion: true });

          if (response && !response?.error) {
            dispatch(updateFormHasPendingChanges(false));
            action();
          } else {
            if(response?.error?.code === OPTIMISTIC_LOCKING_EXCEPTION_ERROR_CODE) {
              const currentValues = formController.form.getFieldsValue();
              handleConflictModalState(
                true,
                {
                  metadata: response?.error.metadata,
                  valuesToSave: currentValues
                },
                action
              );
            }
          }
        } catch (error) {
          console.log('Form validation or other error:', error);

        }
      },
      okText: t('Save Changes'),
      cancelText: t('Quit without saving'),
      title: t('Save Changes?'),
      content: t('Your unsaved changes will be lost. Save changes before closing?')
    });
  };

  const handleCancelConflict = () => {
    setIsConflictModalOpen(false);
    setConflictData(null);
    setPendingAction(null);
  };

  const handleOpenConflictModal = (values = null, action = null) => {

    if(action && typeof action === 'function') {
      setPendingAction(() => action);
    }

    if(values) {
      const { metadata, valuesToSave } = values;
      setConflictData({ metadata, valuesToSave });
      setIsConflictModalOpen(true);
    }
  };

  const handleOverwriteSave = async () => {
    if (!conflictData || !formController) {
      return;
    }

    try {
      const isSuccess = await formController.customSubmit(conflictData.valuesToSave, { forceOverwrite: true });
      if (isSuccess) {
        if (pendingAction) {
          pendingAction();
        }
        dispatch(updateFormHasPendingChanges(false));
      }
    } catch (err) {
      console.error('Failed to overwrite changes:', err);
    } finally {
      handleConflictModalState(false);
    }
  };

  useEffect(() => {
    if(!isLoadingKnowledgeBase) {
      if(knowledgeBase?.createdAt) {
        setActiveTabKey(TABS.ARTICLES.key);

        if (knowledgeBase && knowledgeBase?.languages.length > 0 && !selectedLanguage) {
          dispatch(changeSelectedLanguage(knowledgeBase?.languages.find((language) => language.isPrimary).languageCode));
        }
      } else {
        setActiveTabKey(TABS.SETTINGS.key);
      }
    }

  }, [knowledgeBase, isLoadingKnowledgeBase]);

  const handleUpdateFormInstance = (controller) => {
    setFormController(controller);
  };

  const currentLoadingStates = useMemo(() => ({
    isLoadingKnowledgeBase: isLoadingKnowledgeBase,
    isLoadingArticles: isLoadingArticles,
    isLoadingGroups: isLoadingGroups,
    isLoadingKnowledgeBases: isLoadingKnowledgeBases
  }), [isLoadingKnowledgeBase, isLoadingArticles, isLoadingGroups, isLoadingKnowledgeBases]);

  const isKbIntegrationOrShared = useMemo(
    () => [KNOWLEDGE_BASE_TYPES.INTEGRATION, KNOWLEDGE_BASE_TYPES.SHARED].includes(knowledgeBase?.type),
    [knowledgeBase?.type]
  );

  /**
     * Memoized bundle of data commonly required by the prop-getter functions in the TABS configuration.
     * This helps in optimizing re-renders of `tabItems` and `renderContent` by providing a stable
     * reference unless one of its dependencies changes.
     */
  const commonDataForProps = useMemo(() => ({
    hasEditAccess,
    isKbIntegrationOrShared,
    handleUpdateFormInstance,
    handleConflictModalState
  }), [isKbIntegrationOrShared, hasEditAccess, handleUpdateFormInstance, handleConflictModalState]);

  /**
     * Memoized generation of tab items for the Ant Design Tabs component.
     * It iterates over the TABS configuration, dynamically creating each tab pane
     * with its lazy-loaded component and calculated props.
     * Depends on `activeTabKey` for `suspenseTip` and `commonDataForProps` for lazy component props.
     */
  const tabItems = useMemo(() => {
    return Object.values(TABS)
      .toSorted((a, b) => a.priority - b.priority)
      .map(tab => {
        const TabComponent = tab.LazyTabComponent;
        const tabComponentProps = tab.getTabComponentProps ? tab.getTabComponentProps(commonDataForProps) : {};
        const shouldShowDataLoading = tab.isDataLoading ? tab.isDataLoading(currentLoadingStates) : false;

        return {
          key: tab.key,
          label: tab.getLabel(t),
          children: shouldShowDataLoading ? (
            renderLoadingIndicator('')
          ) : (
            <Suspense fallback={renderLoadingIndicator('')}>
              <TabComponent {...tabComponentProps} />
            </Suspense>
          ),
          disabled: tab.disabled ? tab.disabled(knowledgeBase) : false
        };
      });
  }, [commonDataForProps, currentLoadingStates, knowledgeBase]);

  /**
     * Memoized rendering of the main content area (right panel).
     * It finds the configuration for the currently active tab and renders its
     * `LazyContentComponent` with dynamically generated props.
     * Depends on `activeTabKey` to select the correct component and `commonDataForProps` for its props.
     */
  const renderContent = useMemo(() => {
    const activeTab = Object.values(TABS).find(tab => tab.key === activeTabKey);
    if (activeTab && activeTab.LazyContentComponent) {
      const ContentComponent = activeTab.LazyContentComponent;
      const contentComponentProps = activeTab.getContentComponentProps ? activeTab.getContentComponentProps(commonDataForProps) : {};
      const shouldShowDataLoading = activeTab.isDataLoading ? activeTab.isDataLoading(currentLoadingStates) : false;

      if (shouldShowDataLoading) {
        return renderLoadingIndicator('', '400px');
      }
      return (
        <Suspense fallback={renderLoadingIndicator('', '400px')}>
          <ContentComponent {...contentComponentProps}/>
        </Suspense>
      );
    }
    return null;
  }, [activeTabKey, commonDataForProps, currentLoadingStates]);

  const handleOnTabKeyChange = useCallback((key) => {
    if(key !== activeTabKey) {
      setActiveTabKey(key);
    }
  }, [activeTabKey]);

  const renderTabs = useMemo(() => {
    return (
      <Tabs
        destroyInactiveTabPane
        className={classNames('my-5 ants-tabs-center', {
          'ant-tabs-ink-bar-left': activeTabKey === TABS.ARTICLES.key,
          'ant-tabs-ink-bar-right': activeTabKey === TABS.SETTINGS.key
        })}
        activeKey={activeTabKey}
        onTabClick={(key) => handleShowConfirm(() => handleOnTabKeyChange(key))}
        items={tabItems}
      />
    );
  }, [tabItems, activeTabKey, handleShowConfirm]);

  const handleBackNavigation = () => {
    let targetPathString = replaceUrlParams(NAV_ORGANIZATION_KNOWLEDGE_BASES, { orgId: orgId });

    const [pathname, queryString] = targetPathString.split('?');

    if (queryString) {
      const searchParams = new URLSearchParams(queryString);
      searchParams.delete('article');
      searchParams.delete('group');
      const newQueryString = searchParams.toString();
      targetPathString = pathname + (newQueryString ? `?${newQueryString}` : '');
    }
    navigate(targetPathString);
  };

  return (
    <Fragment>
      <KnowledgeBaseConfirmContext.Provider value={{ handleShowConfirm }}>
        <Row gutter={16} justify="space-between" align="middle" className="mb-2" wrap={false}>
          <Col>
            {
              IS_HRWIZ_CONSOLE_ENABLED ? null : (
                <Space size={16} wrap={false} align="center">
                  <Button
                    type="link"
                    size="large"
                    icon={<ArrowLeftIcon />}
                    className="bg-white shadow fs-base"
                    onClick={() => handleShowConfirm(() => handleBackNavigation())}
                  />
                  <Tooltip title={knowledgeBase?.name?.length > 25 ? `${knowledgeBase?.name}` : ''}>
                    <Typography.Text className="fs-lg">{`${knowledgeBase?.createdAt ? truncate(knowledgeBase?.name, 25, '...') : t('New Knowledge Base')}`}</Typography.Text>
                  </Tooltip>
                </Space>
              )
            }
          </Col>
          <Col flex={'auto'} hidden={activeTabKey !== TABS.ARTICLES.key}>
            <KnowledgeBasesFormLanguageBar />
          </Col>
        </Row>
        <Card className="p-0 knowledge-bases-main-card">
          <ResizablePanelsWrapper
            resetValueTrigger={activeTabKey}
            initialWidth={DEFAULT_SIDE_PANEL_WIDTH}
            minWidth={MIN_SIDE_PANEL_WIDTH}
            maxWidth={MAX_SIDE_PANEL_WIDTH}
            minHeight={655}
            isMobileOrTabletSize={isMobileOrTabletSize}
            isResizingDisabled={activeTabKey === TABS.SETTINGS.key}
          >
            <ResizablePanelsWrapper.LeftPanel>
              {renderTabs}
            </ResizablePanelsWrapper.LeftPanel>
            <ResizablePanelsWrapper.RightPanel>
              {renderContent}
            </ResizablePanelsWrapper.RightPanel>
          </ResizablePanelsWrapper>
        </Card>

        {conflictData && (
          <ConflictChangesModal
            isOpen={isConflictModalOpen}
            onCancel={handleCancelConflict}
            onOverwrite={handleOverwriteSave}
            conflictInfo={conflictData.metadata}
          />
        )}
      </KnowledgeBaseConfirmContext.Provider>
    </Fragment>

  );
};

KnowledgeBasesForm.propTypes = {
  knowledgeBaseId: PropTypes.string
};
export default KnowledgeBasesForm;
